import { useFlashState } from '@st/react-util/use-flash-state'
import {
  Button,
  Dialog,
  DialogButtons,
  DialogTip,
  LinkIcon,
  PlusIcon,
  Spinner,
  Table,
  Tip,
  TrashIcon
} from '@st/theme'
import { formatEntities } from '@st/util/entities'
import {
  saveAndCloseClientOrganizer,
  type STSendClientOrganizerMessage,
  type STSendClientOrganizerView
} from './st-send-client-organizer-module'
import { useEmailsManagedBy } from '~/features/app-hooks'
import { match, P } from '@st/pattern'
import { isValidEmail } from '@st/util/email'

export function STCopyShareableLinkDialog({
  state,
  send,
  onClose
}: {
  state: STSendClientOrganizerView
  send: (message: STSendClientOrganizerMessage) => void
  onClose: () => void
}) {
  const emailsManagedBy = useEmailsManagedBy()

  return match(state)
    .with({ status: 'loading' }, () => (
      <Dialog
        title="Shareable link"
        className="w-full max-w-2xl"
        buttons={
          <DialogButtons>
            <Button variant="subtle" onClick={onClose}>
              Close
            </Button>
          </DialogButtons>
        }
      >
        <div className="flex items-center justify-center py-16">
          <Spinner className="size-8 fill-blue-500 text-blue-100" />
        </div>
      </Dialog>
    ))
    .with({ status: 'loadFailed' }, () => (
      <Dialog
        title="Shareable link"
        className="w-full max-w-2xl"
        buttons={
          <DialogButtons>
            <Button variant="subtle" onClick={onClose}>
              Close
            </Button>
          </DialogButtons>
        }
      >
        <div className="flex flex-col gap-2 py-8 text-sm text-gray-700">
          <div className="font-semibold text-gray-900">Failed to load shareable link.</div>
          <div>Please close this dialog and try again.</div>
        </div>
      </Dialog>
    ))
    .with(
      { status: P.union('loaded', 'saving', 'savingAndClosing', 'sending', 'sent') },
      (state) => (
        <STCopyShareableLinkDialogView
          state={state}
          emailsManagedBy={emailsManagedBy}
          send={send}
          onClose={onClose}
          onSaveFolderAndClose={() => send(saveAndCloseClientOrganizer())}
        />
      )
    )
    .exhaustive()
}

function STCopyShareableLinkDialogView({
  state,
  emailsManagedBy,
  onSaveFolderAndClose,
  onClose,
  send
}: {
  state: Exclude<STSendClientOrganizerView, { status: 'loading' | 'loadFailed' }>
  emailsManagedBy: 'stanfordtax' | 'karbon'
  onClose: () => void
  send: (message: STSendClientOrganizerMessage) => void
  onSaveFolderAndClose: () => void
}) {
  const entities = state.sendState.folder?.entities

  const salutation = entities ? formatEntities(entities ?? {}, 'salutation') + "'s" : 'this'

  return (
    <Dialog
      title={
        entities ? `Shareable link for ${formatEntities(entities, 'client')}` : 'Shareable link'
      }
      subtitle={`The following emails will have access to ${salutation} organizer. Anyone who clicks the shareable link will be asked to verify their email to access the organizer.`}
      className="w-full max-w-2xl"
      tip={
        emailsManagedBy == 'karbon' && (
          <DialogTip>
            {`This is a direct link to this organizer. If you'd like to send ${salutation} link to their Karbon for Clients Portal instead, you can do so via Karbon.`}
          </DialogTip>
        )
      }
      buttons={
        <DialogButtons>
          <Button
            variant="subtle"
            disabled={
              state.status == 'saving' ||
              state.status == 'savingAndClosing' ||
              state.status == 'sending'
            }
            onClick={state.status == 'loaded' ? onSaveFolderAndClose : onClose}
          >
            Close
          </Button>
          <CopyQuestionnaireLink
            url={state.sendState.questionnaireUrl ?? ''}
            onCopyLink={(url) => send({ type: 'copyQuestionnaireLinkRequested', url })}
            disabled={
              state.status == 'saving' ||
              state.status == 'savingAndClosing' ||
              state.status == 'sending'
            }
          />
        </DialogButtons>
      }
    >
      <div className="flex flex-col gap-2">
        <Table>
          <Table.Head>
            <Table.Th>Emails with access</Table.Th>
            <Table.Th className="w-32"></Table.Th>
          </Table.Head>
          <Table.Body>
            {state.editedRecipients.map((recipient) => (
              <Table.Row key={recipient.email} className="group">
                <Table.Cell className="px-6">{recipient.email}</Table.Cell>
                <Table.Cell>
                  <div className="hidden justify-end group-hover:flex">
                    <Tip title="Remove access">
                      <Button
                        variant="subtle"
                        leadingIcon={<TrashIcon className="h-4 w-4" />}
                        disabled={
                          state.status == 'savingAndClosing' ||
                          state.status == 'sending' ||
                          state.status == 'sent'
                        }
                        onClick={() => send({ type: 'emailRemoved', email: recipient.email })}
                      />
                    </Tip>
                  </div>
                </Table.Cell>
              </Table.Row>
            ))}
            <Table.Row>
              <Table.Cell className="px-0" colSpan={2}>
                <form
                  className="flex flex-row items-center"
                  onSubmit={(e) => {
                    e.preventDefault()

                    if (
                      state.status != 'sending' &&
                      state.status != 'sent' &&
                      isValidEmail(state.inputValue)
                    ) {
                      send({ type: 'emailAdded', email: state.inputValue })
                    }
                  }}
                >
                  <PlusIcon className="h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    autoFocus={
                      state.status != 'savingAndClosing' &&
                      state.status != 'sending' &&
                      state.status != 'sent'
                    }
                    autoComplete="off"
                    disabled={
                      state.status == 'savingAndClosing' ||
                      state.status == 'sending' ||
                      state.status == 'sent'
                    }
                    placeholder="Type email address and press Enter..."
                    className="flex-1 border-0 border-transparent px-2 text-base text-gray-900 ring-gray-300 placeholder:text-gray-400 focus:border-transparent focus:ring-0"
                    value={state.inputValue}
                    onChange={(event) => send({ type: 'emailTyped', email: event.target.value })}
                  />
                </form>
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
      </div>
    </Dialog>
  )
}

function CopyQuestionnaireLink({
  url,
  disabled,
  onCopyLink
}: {
  url: string
  disabled: boolean
  onCopyLink: (url: string) => void
}) {
  const [copied, setCopied] = useFlashState(false, 1500)

  return (
    <Button
      variant="primary"
      leadingIcon={<LinkIcon className="h-4 w-4" />}
      disabled={disabled}
      onClick={() => {
        onCopyLink(url)
        setCopied(true)
      }}
    >
      {copied ? 'Copied link' : 'Copy shareable link'}
    </Button>
  )
}
